'use client';

import { Arrow, Portal } from '@radix-ui/react-tooltip';
import type { Route } from 'next';
import Link from 'next/link';
import TableTooltip from '@/components/Table/components/TableTooltip';
import { useIsClipped } from '@/components/Table/hooks/useIsClipped';
import type { LinkItem } from '@/components/Table/types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/ui-utils';

type Props = {
  links: LinkItem[];
  className?: string;
};

export function InternalLinkCell({ links, className }: Props) {
  const [primary, ...rest] = links;
  const [ref, isClipped] = useIsClipped<HTMLAnchorElement>(primary.name);

  if (!primary.id || !primary.linkPath) {
    return <div>-</div>;
  }

  const linkProps = primary.newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {};

  const link = (
    <Link
      ref={ref}
      href={`${primary.linkPath}/${primary.id}` as Route}
      className='block w-full min-w-0 truncate'
      {...linkProps}
    >
      <span
        className={cn(
          `border-b-foreground font-display group-hover:border-b-bui-color-black/50 border-b transition-all
          duration-300`,
          className,
        )}
      >
        {primary.name}
      </span>
    </Link>
  );

  const primaryLink = isClipped ? (
    <TableTooltip description={<span className='whitespace-nowrap'>{primary.name}</span>}>{link}</TableTooltip>
  ) : (
    link
  );

  if (rest.length === 0) return primaryLink;

  return (
    <div className='flex min-w-0 items-center gap-1.5'>
      {primaryLink}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className='bg-background text-foreground border-foreground inline-flex h-6 w-6 shrink-0 cursor-pointer items-center
                justify-center rounded-full border text-xs font-medium leading-none'
            >
              <span>+{rest.length}</span>
            </span>
          </TooltipTrigger>
          <Portal>
            <TooltipContent
              side='bottom'
              sideOffset={6}
              className='bg-popover overflow-visible p-0 text-sm
                filter-[drop-shadow(0_4px_6px_rgb(0_0_0/0.08))_drop-shadow(0_2px_4px_rgb(0_0_0/0.06))]'
            >
              <Arrow className='fill-popover' />
              <div className='flex flex-col py-1'>
                {rest.map((item) => (
                  <Link
                    key={item.id}
                    href={`${item.linkPath}/${item.id}` as Route}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-foreground group px-3 py-1 whitespace-nowrap'
                  >
                    <span
                      className={cn(
                        `border-b-foreground font-display group-hover:border-b-bui-color-black/50 border-b transition-all
                        duration-300`,
                        className,
                      )}
                    >
                      {item.name}
                    </span>
                  </Link>
                ))}
              </div>
            </TooltipContent>
          </Portal>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
